import type { Address, OrderWithDetails } from "@estelle/domain";
import type { ReactNode } from "react";

import { PhoneNumber } from "@/components/phone/PhoneNumber";
import { cn } from "@/lib/utils";
import { formatMoney } from "@/components/table/cells";
import { CustomerLink } from "@/modules/contacts/customers/screens/CustomerLink";
import { paymentFact } from "@/modules/orders/facts";

function Fact({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "min-w-0 px-4 py-3 max-md:rounded-lg max-md:border md:border-r md:last:border-r-0",
        className,
      )}
    >
      <p className="text-muted-foreground text-[11px] font-semibold tracking-wide uppercase">
        {label}
      </p>
      <div className="mt-1 min-w-0">{children}</div>
    </div>
  );
}

function present(part: string | undefined): part is string {
  return Boolean(part && part.trim() !== "");
}

/**
 * The shipping address as lines, blank parts dropped: recipient, company,
 * street, then `City, ST PIN`, then country (#653).
 */
function shipToLines(address: Address): string[] {
  const stateAndPin = [address.provinceCode ?? address.province, address.zip]
    .filter(present)
    .join(" ");
  const cityRow = [address.city, stateAndPin].filter(present).join(", ");
  return [
    address.name,
    address.company,
    address.line1,
    address.line2,
    cityRow,
    address.country,
  ].filter(present);
}

/**
 * The three facts someone opens an order for, in one strip under the title
 * (#644): who (with the Call button, #635), where it goes (the whole
 * address, #653), and what to collect. What is in the order is the Items
 * tab, so it is not repeated here.
 *
 * On a phone the strip is a single column. `callSlot` is where the call
 * log (#636) puts earlier attempts.
 */
export function OrderFacts({
  order,
  shippingMethod,
  onCall,
  callSlot,
}: {
  order: OrderWithDetails;
  shippingMethod: string | null;
  onCall?: () => Promise<void> | void;
  callSlot?: ReactNode;
}) {
  const address = order.shippingAddress;
  const name =
    order.customer?.displayName ??
    order.customerName ??
    address?.name ??
    "Guest checkout";
  const phone = order.customer?.phone ?? address?.phone ?? null;
  const lines = address ? shipToLines(address) : [];
  const pay = paymentFact(order);

  return (
    <section
      aria-label="Order at a glance"
      className="bg-background grid gap-2 md:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)_minmax(0,1fr)] md:gap-0 md:rounded-lg md:border"
    >
      <Fact label="Customer">
        <p className="truncate text-[15px] font-semibold">
          {order.customer ? (
            <CustomerLink customerId={order.customer.id}>{name}</CustomerLink>
          ) : (
            name
          )}
        </p>
        {phone ? (
          <div className="text-muted-foreground mt-0.5 text-sm">
            <PhoneNumber
              number={phone}
              name={name}
              onCall={onCall}
              slot={callSlot}
            />
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">No phone number</p>
        )}
      </Fact>
      <Fact label="Ship to">
        {lines.length > 0 ? (
          <address className="text-sm not-italic">
            {lines.map((line, index) => (
              <p
                key={line}
                className={cn(
                  "break-words",
                  index === 0 && "text-[15px] font-semibold",
                )}
              >
                {line}
              </p>
            ))}
          </address>
        ) : (
          <p className="text-muted-foreground text-sm">No shipping address</p>
        )}
        <p className="text-muted-foreground mt-1 text-sm">
          {shippingMethod ?? (
            // A gap in the data, not an action: a quiet note, not a bar (#644).
            <span className="text-muted-foreground/80 text-xs">
              Shipping method not set
            </span>
          )}
        </p>
      </Fact>
      <Fact label="Payment">
        <p
          className={cn(
            "truncate text-[15px] font-semibold tabular-nums",
            pay.tone === "collect" && "text-destructive",
          )}
        >
          {pay.headline}
        </p>
        <p className="text-muted-foreground truncate text-sm">
          {[
            pay.detail,
            pay.tone === "settled" ? formatMoney(order.total) : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
      </Fact>
    </section>
  );
}
